/**
 * Ask: a question about the owner's own site, answered free (the owner's decision D4,
 * 2026-09-30; the server's contract C4, infra/lambda/handlers/ask.ts).
 *
 * Chat only ever edited, so "How many people RSVP'd?" was charged as a change to the page (F26,
 * deep-dive 2026-09-30). The answer comes from the site's code and the rows its lists hold right
 * now, which this client reads through the same `/apps/{id}/{collection}` route Your data → Live
 * and the replies screen already read — the server does not read them a second way.
 */
// Pure: which collections a page submits into, keeps per person, or lets its owner fill.
import { publishedSchemas } from '@/webapp/schemasFromSource';
import { ApiError, request } from './client';

/** The contract's limits: what one question is asked with. */
export const ASK_MAX_LISTS = 5;
export const ASK_MAX_ROWS = 200;

export interface AskRows {
  list: string;
  items: unknown[];
  /**
   * Whether the list had rows past this page. Sent every time: the server reads an absent
   * `more` as "there may be more", and only `false` lets it state an exact count.
   */
  more: boolean;
}

/**
 * The rows a question is asked with: the replies people sent (`S.submit`) first — "how many
 * RSVP'd" is the question that started this — then the lists the owner keeps (`S.list`). Never
 * `S.cloud`: those are each visitor's own rows, and the owner is not shown them anywhere else.
 *
 * The first page of each, one request per list and no retry: a question is worth asking with
 * what is at hand. A list that could not be read is left OUT rather than sent empty, because an
 * empty list is a fact the answer would repeat ("nobody has replied") and a missing one is not.
 */
export async function liveRowsForAsk(hostedAppId: string, source: string): Promise<AskRows[]> {
  const lists = publishedSchemas(source)
    .filter((s) => s.ownership !== 'user')
    .map((s) => s.name)
    .filter((name, i, all) => all.indexOf(name) === i)
    .slice(0, ASK_MAX_LISTS);
  const out: AskRows[] = [];
  for (const list of lists) {
    try {
      const body = await request<{ items?: unknown[]; cursor?: string } | undefined>(
        `/apps/${encodeURIComponent(hostedAppId)}/${encodeURIComponent(list)}?limit=${ASK_MAX_ROWS}`,
        { retry: false },
      );
      const items = body?.items ?? [];
      /*
       * The cursor, not the row count, says whether there is more. A page can come back short
       * with a cursor — the route drops deleted rows after its 200-row query, and a DynamoDB
       * Query stops at 1MB — and 196 rows of 300 RSVPs were told to the model as the count
       * when the cursor was thrown away here (review, 2026-09-30).
       */
      out.push({ list, items: items.slice(0, ASK_MAX_ROWS), more: Boolean(body?.cursor) || items.length > ASK_MAX_ROWS });
    } catch {
      /* left out — see above */
    }
  }
  return out;
}

/**
 * Ask, and hand back the answer. A refusal the server explains — the day's limit, a site that
 * is not this account's, one not published yet — is thrown in the server's own sentence, which
 * says more than the client's generic one for its status.
 *
 * The site's code is not sent: the server reads the page as published from the site's record
 * and ignores any code a request carries, because believing it made /ask a free prompt for any
 * signed-in account (review, 2026-09-30). The source stays here, where it picks the lists.
 */
export async function askAboutSite(input: {
  appId: string;
  question: string;
  rows: AskRows[];
}): Promise<string> {
  try {
    const body = await request<{ answer?: string }>('/ask', {
      method: 'POST',
      body: { appId: input.appId, question: input.question, rows: input.rows },
      // Inside the gateway's 29 seconds, which the server stops short of.
      timeoutMs: 32_000,
    });
    return (body?.answer ?? '').trim() || "I can't tell from your site.";
  } catch (err) {
    if (err instanceof ApiError && err.reason && err.kind !== 'unauthorized' && /\s/.test(err.reason)) {
      throw new ApiError(err.kind, err.reason, err.status, err.reason, { code: err.code });
    }
    throw err;
  }
}
